<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useConfigurationsStore } from "../../../../stores";
import AppModal from "@/components/ui/AppModal.vue";
import NewProfessionalBody
  from "@/domains/home/services/configurations/views/settings/insolvency/proffessionalBodies/NewProfessionalBody.vue";
import EditProfessionalBody
  from "@/domains/home/services/configurations/views/settings/insolvency/proffessionalBodies/EditProfessionalBody.vue";

interface ProfessionalBody {
  id: string
  name: string
  abbreviation: string
  profession: string
}

const store = useConfigurationsStore();
const showAddBody = ref(false)
const showEditBody = ref(false)
const professional_bodies = ref<ProfessionalBody[]>([])
const draft = ref<ProfessionalBody | null>(null)

const rows = {
  odd: 'border-t border-gray-200 hover:bg-gray-10',
  even: 'border-t border-gray-200 bg-gray-10'
}

const professionalBodies = computed(() => store.professionalBodies);

const openForm = () => {
  showAddBody.value = true
}

const openEditForm = (body: ProfessionalBody) => {
  draft.value = body
  showEditBody.value = true
}

onMounted(() => {
  store.fetchProfessionalBodies();
})

watch(professionalBodies, (data) => {
  if (data?.data) {
    professional_bodies.value = data.data
  }
})
</script>

<template>
  <div class="flex flex-col">
    <!-- Modals -->
    <AppModal v-model="showAddBody" title="Add Professional Body" hide-footer>
      <NewProfessionalBody />
    </AppModal>
    <AppModal v-model="showEditBody" title="Update Professional Body" hide-footer>
      <EditProfessionalBody :draft-data="draft" />
    </AppModal>

    <!-- Action Bar -->
    <div class="flex items-center gap-2 justify-end mb-4">
      <button
        @click="openForm"
        class="btn-primary">
        <i class="fa-solid fa-plus"></i>
        <span>Add Body</span>
      </button>
    </div>

    <!-- Table -->
    <div class="bg-white rounded border border-gray-200 overflow-hidden flex-1">
      <div class="overflow-x-auto">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">#</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Name</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Abbreviation</th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Profession</th>
              <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(pb, idx) in professional_bodies" :key="pb.id" :class="[(idx % 2) ? rows.even : rows.odd]">
              <td class="py-3 px-4 border-b border-gray-200">{{ idx + 1 }}</td>
              <td class="py-3 px-4 border-b border-gray-200 font-bold text-capitalize">{{ pb.name }}</td>
              <td class="py-3 px-4 border-b border-gray-200 font-bold text-capitalize">{{ pb.abbreviation }}</td>
              <td class="py-3 px-4 border-b border-gray-200 font-bold text-capitalize">{{ pb.profession }}</td>
              <td class="py-3 px-4 border-b border-gray-200">
                <div class="flex items-center justify-center space-x-2">
                  <button
                    @click="openEditForm(pb)"
                    class="btn-sm-primary"
                  >
                    <i class="fa-solid fa-pencil"></i>
                    <span>Edit</span>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="professional_bodies.length === 0">
              <td colspan="5" class="py-4 text-center text-gray-500">
                No professional bodies found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
